<script setup>
// Lista das landing pages do endereco da conta. Cada pagina abre em tela
// PROPRIA (settings_landing_pages_edit); as configuracoes do site (dominio,
// rastreamento, icone) moram em settings_landing_pages_site.
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast } from 'dashboard/composables/useToast';
import { useAccount } from 'dashboard/composables/useAccount';
import { useRouter } from 'vue-router';
import LandingPagesAPI from 'dashboard/api/landingPages';
import SettingsPageLayout from 'dashboard/components-next/Settings/SettingsPageLayout.vue';
import SettingsTable from 'dashboard/components-next/Settings/SettingsTable.vue';
import SettingsListToolbar from 'dashboard/components-next/Settings/SettingsListToolbar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import format from 'date-fns/format';
import { useLandingPageSite } from './composables/useLandingPageSite';

const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const router = useRouter();

const DOMAIN_SUFFIX = '.smart2.com.br';
const { site, pages, isLoading, hasSite, isProvisioned, apiError, fetchSite } =
  useLandingPageSite();

const isSaving = ref(false);
const subdomainInput = ref('');
const searchQuery = ref('');

const filteredPages = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return pages.value;
  return pages.value.filter(
    page =>
      page.title?.toLowerCase().includes(q) ||
      page.slug?.toLowerCase().includes(q)
  );
});

// ── Acompanha provisionamento/publicacao em andamento ──
let pollTimer = null;
const hasWorkInFlight = () =>
  site.value?.status === 'pending' ||
  pages.value.some(page => page.status === 'publishing');

const ensurePolling = () => {
  if (pollTimer) return;
  pollTimer = setInterval(async () => {
    await fetchSite({ silent: true });
    if (!hasWorkInFlight()) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }, 4000);
};

const createSite = async () => {
  if (!subdomainInput.value) return;
  isSaving.value = true;
  try {
    const { data } = await LandingPagesAPI.createSite({
      subdomain: subdomainInput.value.trim().toLowerCase(),
    });
    site.value = data;
    toast.success(t('LANDING_PAGES.SITE.PROVISIONING_STARTED'));
    ensurePolling();
  } catch (error) {
    toast.error(apiError(error));
  } finally {
    isSaving.value = false;
  }
};

const retryProvision = async () => {
  isSaving.value = true;
  try {
    const { data } = await LandingPagesAPI.provisionSite(site.value.id);
    site.value = data;
    toast.success(t('LANDING_PAGES.SITE.PROVISIONING_STARTED'));
    ensurePolling();
  } catch (error) {
    toast.error(apiError(error));
  } finally {
    isSaving.value = false;
  }
};

// ── Navegacao ──
const goToSiteSettings = () =>
  router.push(accountScopedRoute('settings_landing_pages_site'));
const goToNewPage = () =>
  router.push(accountScopedRoute('settings_landing_pages_new'));
const goToPage = (page, tab) =>
  router.push({
    ...accountScopedRoute('settings_landing_pages_edit', { pageId: page.id }),
    query: tab ? { tab } : {},
  });

// ── Acoes da pagina ──
const runPageAction = async (action, successKey) => {
  isSaving.value = true;
  try {
    await action();
    toast.success(t(successKey));
    await fetchSite({ silent: true });
    ensurePolling();
  } catch (error) {
    toast.error(apiError(error));
  } finally {
    isSaving.value = false;
  }
};

const publishPage = page =>
  runPageAction(
    () => LandingPagesAPI.publishPage(site.value.id, page.id),
    'LANDING_PAGES.PAGES.PUBLISH_QUEUED'
  );

const unpublishPage = page =>
  runPageAction(
    () => LandingPagesAPI.unpublishPage(site.value.id, page.id),
    'LANDING_PAGES.PAGES.UNPUBLISHED'
  );

const deletePage = page => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('LANDING_PAGES.PAGES.DELETE_CONFIRM'))) return;
  runPageAction(
    () => LandingPagesAPI.deletePage(site.value.id, page.id),
    'LANDING_PAGES.PAGES.DELETED'
  );
};

const openPreview = page => window.open(page.preview_url, '_blank', 'noopener');

// LP-modelo: 1 por site (o backend desmarca a irmã) — o copiloto usa a
// estrutura dela como esqueleto das páginas novas.
const toggleTemplate = page => {
  runPageAction(
    () =>
      LandingPagesAPI.updatePage(site.value.id, page.id, {
        is_template: !page.is_template,
      }),
    page.is_template
      ? 'LANDING_PAGES.PAGES.TEMPLATE_REMOVED'
      : 'LANDING_PAGES.PAGES.TEMPLATE_MARKED'
  );
};

const fmtDate = ts => (ts ? format(new Date(ts * 1000), 'dd/MM HH:mm') : '—');

const statusBadge = status => {
  const map = {
    published: {
      key: 'STATUS_PUBLISHED',
      cls: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    },
    publishing: {
      key: 'STATUS_PUBLISHING',
      cls: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    },
    publish_failed: {
      key: 'STATUS_FAILED',
      cls: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    },
  };
  const entry = map[status] || {
    key: 'STATUS_DRAFT',
    cls: 'bg-n-slate-3 text-n-slate-11',
  };
  return { label: t(`LANDING_PAGES.PAGES.${entry.key}`), cls: entry.cls };
};

const pageHeaders = computed(() => [
  t('LANDING_PAGES.PAGES.COL_TITLE'),
  t('LANDING_PAGES.PAGES.COL_URL'),
  t('LANDING_PAGES.PAGES.COL_STATUS'),
  { label: t('LANDING_PAGES.PAGES.COL_CONVERSIONS'), align: 'right' },
  { label: t('LANDING_PAGES.PAGES.COL_LINKS'), align: 'right' },
  { label: t('LANDING_PAGES.PAGES.COL_UPDATED'), align: 'right' },
  '',
]);

onMounted(async () => {
  await fetchSite();
  if (hasWorkInFlight()) ensurePolling();
});
onUnmounted(() => {
  if (pollTimer) clearInterval(pollTimer);
});
</script>

<template>
  <SettingsPageLayout
    :title="t('LANDING_PAGES.HEADER')"
    :description="hasSite ? '' : t('LANDING_PAGES.DESCRIPTION')"
    :kicker="t('LANDING_PAGES.KICKER')"
  >
    <!-- Endereco do site + status moram no cabecalho (nao repetem no corpo) -->
    <template v-if="hasSite && site" #description>
      <a
        :href="site.url"
        target="_blank"
        rel="noopener noreferrer"
        class="font-mono text-[13px] font-semibold text-n-slate-12 hover:underline"
      >
        {{ site.hostname }}
      </a>
      <span
        class="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ml-1.5 align-middle"
        :class="
          isProvisioned
            ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
        "
      >
        {{
          isProvisioned
            ? t('LANDING_PAGES.SITE.STATUS_ACTIVE')
            : t('LANDING_PAGES.SITE.STATUS_PENDING')
        }}
      </span>
    </template>
    <template #actions>
      <template v-if="hasSite">
        <Button
          :label="t('LANDING_PAGES.SITE.SETTINGS')"
          icon="i-lucide-settings"
          variant="outline"
          color="slate"
          size="sm"
          @click="goToSiteSettings"
        />
        <Button
          :label="t('LANDING_PAGES.PAGES.NEW')"
          icon="i-lucide-plus"
          size="sm"
          :disabled="!isProvisioned"
          @click="goToNewPage"
        />
      </template>
    </template>

    <div v-if="isLoading" class="flex justify-center py-16">
      <Spinner :size="28" class="text-n-brand" />
    </div>

    <!-- Primeiro passo: criar o endereco -->
    <div
      v-else-if="!hasSite"
      class="rounded-xl border border-n-weak bg-n-solid-1 p-6 max-w-[34rem]"
    >
      <h3
        class="font-headline text-[14.5px] font-semibold text-n-slate-12 mb-1"
      >
        {{ t('LANDING_PAGES.SITE.TITLE') }}
      </h3>
      <p class="text-[13px] text-n-slate-10 mb-4">
        {{ t('LANDING_PAGES.SITE.DESCRIPTION') }}
      </p>
      <div class="flex items-end gap-3 flex-wrap">
        <div class="flex items-end gap-2">
          <div class="w-56">
            <Input
              v-model="subdomainInput"
              :label="t('LANDING_PAGES.SITE.SUBDOMAIN_LABEL')"
              :placeholder="t('LANDING_PAGES.SITE.SUBDOMAIN_PLACEHOLDER')"
            />
          </div>
          <span class="text-[13px] text-n-slate-10 pb-2.5">
            {{ DOMAIN_SUFFIX }}
          </span>
        </div>
        <Button
          :label="t('LANDING_PAGES.SITE.CREATE')"
          :is-loading="isSaving"
          @click="createSite"
        />
      </div>
    </div>

    <template v-else>
      <!-- Provisionamento pendente/travado: retry + erro (endereco + status
           moram no cabecalho da pagina) -->
      <div
        v-if="!isProvisioned || site.provision_error"
        class="flex items-center gap-3 flex-wrap mb-5 pb-5 border-b border-n-weak"
      >
        <Button
          v-if="!isProvisioned"
          :label="t('LANDING_PAGES.SITE.RETRY')"
          size="sm"
          variant="outline"
          :is-loading="isSaving"
          @click="retryProvision"
        />
        <span
          v-if="site.provision_error"
          class="text-[12px] text-n-ruby-9 w-full"
        >
          {{ site.provision_error }}
        </span>
      </div>

      <SettingsListToolbar
        v-model="searchQuery"
        :placeholder="t('LANDING_PAGES.PAGES.SEARCH')"
        :count-label="t('LANDING_PAGES.PAGES.COUNT_PAGES', pages.length)"
      />
      <SettingsTable
        :headers="pageHeaders"
        :is-empty="!filteredPages.length"
        :empty-message="t('LANDING_PAGES.PAGES.EMPTY')"
      >
        <tr
          v-for="page in filteredPages"
          :key="page.id"
          class="group hover:bg-n-solid-2 transition-colors cursor-pointer"
          @click="goToPage(page)"
        >
          <td class="px-5 py-3">
            <span
              class="font-medium text-n-slate-12 text-sm inline-flex items-center gap-1.5"
            >
              {{ page.title || page.slug }}
              <span
                v-if="page.is_template"
                class="inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-full bg-n-brand/10 text-n-brand"
                :title="t('LANDING_PAGES.PAGES.TEMPLATE_HINT')"
              >
                <span class="i-lucide-star size-2.5" />
                {{ t('LANDING_PAGES.PAGES.TEMPLATE_BADGE') }}
              </span>
            </span>
            <span class="block text-[11px] font-mono text-n-slate-9 mt-0.5">
              {{ page.conversion_event }}
            </span>
          </td>
          <td class="px-5 py-3">
            <a
              :href="page.public_url"
              target="_blank"
              rel="noopener noreferrer"
              class="font-mono text-[12.5px] text-n-slate-11 hover:underline"
              :title="page.public_url"
              @click.stop
            >
              /{{ page.slug }}
            </a>
          </td>
          <td class="px-5 py-3 whitespace-nowrap">
            <span
              v-if="page.unpublished_changes && page.status === 'published'"
              class="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
              :title="page.publish_error || ''"
            >
              {{ t('LANDING_PAGES.PAGES.UNPUBLISHED_CHANGES') }}
            </span>
            <span
              v-else
              class="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium"
              :class="statusBadge(page.status).cls"
              :title="page.publish_error || ''"
            >
              {{ statusBadge(page.status).label }}
            </span>
          </td>
          <td class="px-5 py-3 text-right whitespace-nowrap">
            <template v-if="page.status === 'draft'">
              <span class="text-[12.5px] text-n-slate-9">—</span>
            </template>
            <template v-else>
              <b class="text-sm tabular-nums text-n-slate-12">
                {{ page.stats?.conversions ?? 0 }}
              </b>
              <span
                v-if="page.stats?.trend != null && page.stats.trend !== 0"
                class="text-[11px] tabular-nums ml-1"
                :class="
                  page.stats.trend > 0 ? 'text-n-teal-10' : 'text-n-ruby-9'
                "
              >
                {{ page.stats.trend > 0 ? '▲' : '▼' }}
                {{ Math.abs(page.stats.trend) }}%
              </span>
            </template>
          </td>
          <td class="px-5 py-3 text-right">
            <button
              class="text-sm tabular-nums text-n-slate-11 hover:text-n-slate-12 hover:underline"
              :title="t('LANDING_PAGES.UTM.TITLE')"
              @click.stop="goToPage(page, 'links')"
            >
              {{ page.campaign_links?.length || 0 }}
            </button>
          </td>
          <td
            class="px-5 py-3 text-right text-[12px] tabular-nums text-n-slate-10 whitespace-nowrap"
          >
            {{ fmtDate(page.updated_at) }}
          </td>
          <td class="px-5 py-3">
            <span
              class="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
              @click.stop
            >
              <Button
                v-if="page.status !== 'published' || page.unpublished_changes"
                :label="
                  page.unpublished_changes
                    ? t('LANDING_PAGES.PAGES.REPUBLISH')
                    : t('LANDING_PAGES.PAGES.PUBLISH')
                "
                size="xs"
                variant="outline"
                color="teal"
                :disabled="!isProvisioned || page.status === 'publishing'"
                @click="publishPage(page)"
              />
              <Button
                v-if="page.status === 'published'"
                :label="t('LANDING_PAGES.PAGES.UNPUBLISH')"
                size="xs"
                variant="outline"
                color="slate"
                @click="unpublishPage(page)"
              />
              <Button
                :icon="page.is_template ? 'i-lucide-star-off' : 'i-lucide-star'"
                size="xs"
                variant="ghost"
                color="slate"
                :title="
                  page.is_template
                    ? t('LANDING_PAGES.PAGES.TEMPLATE_UNSET')
                    : t('LANDING_PAGES.PAGES.TEMPLATE_SET')
                "
                @click="toggleTemplate(page)"
              />
              <Button
                icon="i-lucide-eye"
                size="xs"
                variant="ghost"
                color="slate"
                :title="t('LANDING_PAGES.PAGES.PREVIEW')"
                @click="openPreview(page)"
              />
              <Button
                icon="i-lucide-trash-2"
                size="xs"
                variant="ghost"
                color="ruby"
                @click="deletePage(page)"
              />
            </span>
          </td>
        </tr>
      </SettingsTable>
    </template>
  </SettingsPageLayout>
</template>
